import { useLayoutEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import type {
  AdminReferralRow,
  AdminSendOutcome,
  AdminUserKyc,
  MemberRole,
} from "../api/types";
import { ActivityPanel } from "../components/ActivityPanel";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DeviceBindingSection } from "../components/DeviceBindingSection";
import { CopyButton } from "../components/CopyButton";
import { ErrorNotice, Loading } from "../components/Notice";
import { PageHead } from "../components/PageHead";
import { PortfolioPanel } from "../components/PortfolioPanel";
import { Section } from "../components/Section";
import { SendComposer } from "../components/SendComposer";
import { KycChip, ReadChip, RoleChip } from "../components/StatusChip";
import { formatDate, formatDateTime, orDash } from "../format";
import { useAsync, useCompact } from "../hooks";
import { patchBookRow } from "../lib/book-snapshot";
import { openedFrom, SECTIONS } from "../sections";
import { useSession } from "../session/SessionContext";

export function UserDetail() {
  const { userId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const compact = useCompact();
  const { api, staff: me } = useSession();

  // Arriving from the table carries the row in router state; a direct hit (a
  // bookmark, a refresh) reads it by id. Both are the same row shape from the
  // same predicate, so the header cannot disagree with the list.
  const passedRow = (location.state as { row?: AdminReferralRow } | null)?.row ?? null;

  const lookup = useAsync(
    () => (passedRow ? Promise.resolve(passedRow) : api.member(userId)),
    [api, userId, passedRow],
  );
  const portfolio = useAsync(() => api.memberPortfolio(userId), [api, userId]);
  const screens = useAsync(() => api.notificationScreens(), [api]);
  const chains = useAsync(() => api.chains(), [api]);
  // Its own read, not a column on the referral row: this is per-user detail on
  // a list endpoint that serves 100 rows a page.
  const kyc = useAsync(() => api.userKyc(userId), [api, userId]);
  // Filtered server-side: paging one shared list and matching the recipient
  // client-side dropped every send older than the first page.
  const outcomes = useAsync(
    () => api.outcomes({ recipientUserId: userId }),
    [api, userId],
  );

  const row = lookup.data;
  const mine = outcomes.data?.items ?? [];

  // A member opens at the top. The router keeps the window's scroll across a
  // push, so a row opened far down the book used to land halfway down this
  // page.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [userId]);

  // Back is a step back through history when a list is what is behind this
  // page (the link that opened it said where from, in router state), so the
  // list comes back as it was left and a phone's back swipe agrees with the
  // trail. A bookmark or a refresh has no list behind it, and gets a plain
  // link.
  const from = SECTIONS[openedFrom(location.state)];
  const cameFromList = location.state != null;
  function back(event: React.MouseEvent) {
    if (!cameFromList || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    navigate(-1);
  }

  return (
    <div className="page">
      <PageHead
        title={orDash(row?.email ?? null)}
        trail={[{ label: from.label, to: from.to, onClick: back }]}
        aside={
          // The role sits in the header because it explains the funnel below
          // it: a staff row is listed but never counted. Only an admin can
          // change it, and never on their own row, because the API refuses
          // both writes against yourself with a 409 and a control that exists
          // only to be refused is worse than no control.
          row ? (
            <RoleControl
              key={row.privyDid}
              row={row}
              canChange={me?.role === "admin" && row.privyDid !== me.privyDid}
            />
          ) : null
        }
      >
        <div className="row">
          <span className="subtle">{orDash(row?.name ?? null)}</span>
          <span className="mono-inline mono-id">{row?.privyDid ?? userId}</span>
          <CopyButton value={row?.privyDid ?? userId} label="Copy ID" />
        </div>
      </PageHead>

      <Section
        title="Funnel"
        aside={row ? <span className="subtle">Code {orDash(row.code)}</span> : null}
      >
        <div className="panel-body">
          {lookup.loading ? <Loading what="status" /> : null}
          <ErrorNotice error={lookup.error} />
          {row ? <FunnelSteps row={row} /> : null}
        </div>
      </Section>

      <Section
        title="KYC"
        aside={
          kyc.data?.status ? (
            <span className="subtle mono-inline">{kyc.data.status}</span>
          ) : null
        }
      >
        <div className="panel-body">
          {kyc.loading ? <Loading what="KYC" /> : null}
          <ErrorNotice error={kyc.error} />
          {kyc.data ? <KycCase kyc={kyc.data} /> : null}
        </div>
      </Section>

      {/* Admin only, and not on your own row: the API refuses both. */}
      {me?.role === "admin" && row && row.privyDid !== me.privyDid ? (
        <DeviceBindingSection userId={userId} />
      ) : null}

      <PortfolioPanel
        portfolio={portfolio.data}
        loading={portfolio.loading}
        error={portfolio.error}
      />

      <ActivityPanel api={api} userId={userId} />

      <Section
        title="Send a notification"
        aside={<span className="subtle">One recipient, one push.</span>}
      >
        <div className="panel-body">
          {screens.loading || chains.loading ? (
            <Loading what="destinations" />
          ) : (
            <SendComposer
              userId={userId}
              screens={screens.data?.screens ?? []}
              screensError={screens.error ?? chains.error}
              chains={chains.data?.chains ?? []}
              holdings={portfolio.data?.wallet.tokens ?? []}
              onPreview={(body) => api.previewNotification(body)}
              onSend={async (body) => {
                await api.sendNotification(body);
                // The send response is optimistic; the outcomes row is the
                // only evidence, so the history is re-read once it lands.
                outcomes.reload();
              }}
            />
          )}
        </div>
      </Section>

      <Section title="Sent to this user" aside={<Link to="/notifications">All notifications</Link>}>
        {outcomes.loading ? (
          <Loading what="notifications" />
        ) : mine.length === 0 ? (
          <div className="empty">
            <strong>Nothing sent yet</strong>
            <p className="subtle">Sends to this user will be listed here.</p>
          </div>
        ) : (
          <UserOutcomes items={mine} compact={compact} />
        )}
      </Section>
    </div>
  );
}

/**
 * The member's role, and for an admin the way to change it.
 *
 * The Staff screen can do this too, but it asks for an email, which is an
 * address you are already looking at, retyped, and re-resolved against a
 * column where it is not unique. Here the target is the DID, so the
 * shared-email 409 cannot happen at all.
 *
 * The change goes through a dialog because it grants or removes console
 * access, and a button left in the page was easy to hit by accident. The
 * dialog offers the two roles the member does not hold. Behind it there are
 * still two writes: a grant writes 'rm' or 'admin', and 'user' is only reached
 * by revoking, which never deletes the row (an owned invite code keeps its FK
 * target).
 */
function RoleControl({ row, canChange }: { row: AdminReferralRow; canChange: boolean }) {
  const [role, setRole] = useState<MemberRole>(row.role);
  const [open, setOpen] = useState(false);

  return (
    <div className="row page-head-aside">
      <RoleChip role={role} />
      {canChange ? (
        <button type="button" onClick={() => setOpen(true)}>
          Change role
        </button>
      ) : null}
      {open ? (
        <RoleDialog
          row={row}
          current={role}
          onDone={(written) => {
            setRole(written);
            setOpen(false);
            // A grant also redeems the invite; a revoke leaves it as it was.
            patchBookRow(row.userId, {
              role: written,
              isStaff: written !== "user",
              ...(written !== "user" ? { inviteRedeemed: true } : {}),
            });
          }}
          onCancel={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

const ROLE_OPTIONS: { role: MemberRole; label: string; detail: string }[] = [
  { role: "admin", label: "Admin", detail: "Full console access, including changing staff roles." },
  { role: "rm", label: "RM", detail: "Console access without staff management." },
  {
    role: "user",
    label: "User",
    detail: "Revokes console access. They stay in the book and count in the funnel again.",
  },
];

const CONFIRM_LABEL: Record<MemberRole, string> = {
  admin: "Make admin",
  rm: "Make RM",
  user: "Revoke to user",
};

function RoleDialog({
  row,
  current,
  onDone,
  onCancel,
}: {
  row: AdminReferralRow;
  current: MemberRole;
  onDone: (written: MemberRole) => void;
  onCancel: () => void;
}) {
  const { api } = useSession();
  // Nothing picked on open, so the confirm starts disabled and the dialog
  // cannot be answered by the Enter that opened it.
  const [next, setNext] = useState<MemberRole | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function apply() {
    if (busy || next === null) return;
    setBusy(true);
    setError(null);
    try {
      // The role comes back from the API rather than from what we asked for:
      // it is the row that was actually written.
      const written =
        next === "user"
          ? (await api.revokeStaff(row.privyDid)).role
          : (await api.grantStaff({ privyDid: row.privyDid, role: next })).role;
      onDone(written);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <ConfirmDialog
      title="Change role"
      confirmLabel={next ? CONFIRM_LABEL[next] : "Change role"}
      busyLabel="Saving..."
      busy={busy}
      confirmDisabled={next === null}
      danger={next === "user"}
      onConfirm={() => void apply()}
      onCancel={onCancel}
    >
      <p>
        {orDash(row.email)} is currently <RoleChip role={current} />
      </p>
      <fieldset className="role-options" disabled={busy}>
        <legend>Change to</legend>
        {ROLE_OPTIONS.filter((option) => option.role !== current).map((option) => (
          <label key={option.role} className="role-option">
            <input
              type="radio"
              name="next-role"
              value={option.role}
              checked={next === option.role}
              onChange={() => setNext(option.role)}
            />
            <span className="stack">
              <strong>{option.label}</strong>
              <span className="subtle">{option.detail}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {current === "user" ? (
        <p className="subtle">
          Granting a role also marks their invite as redeemed, so they leave the
          waitlist count.
        </p>
      ) : null}
      <ErrorNotice error={error} />
    </ConfirmDialog>
  );
}

/**
 * One user's KYC case, for an operator answering "why am I stuck?".
 *
 * `reasons` is the provider's own wording for the cancellation, and the API
 * only sends it while the case is actually canceled — a case Interlace
 * overturned keeps its old reason text upstream, so anything shown here for a
 * passing user would be a live-looking report of a failure that is over.
 *
 * The case id is copyable because it is the handle Interlace is asked about:
 * an escalation is that string plus what is written below it.
 */
function KycCase({ kyc }: { kyc: AdminUserKyc }) {
  if (kyc.verdict === null) {
    return (
      <p className="subtle">
        This user has not started verification, so there is no case to look at
        yet.
      </p>
    );
  }

  return (
    <div className="stack">
      <div className="row">
        <KycChip status={kyc.verdict} />
        {kyc.updatedAt ? (
          <span className="subtle">
            Last change {formatDateTime(kyc.updatedAt)}
          </span>
        ) : null}
      </div>

      {kyc.reasons.length > 0 ? (
        <div className="stack">
          <span className="subtle">What the identity check flagged</span>
          <ul className="reasons">
            {kyc.reasons.map((reason, index) => (
              <li key={index}>{reason}</li>
            ))}
          </ul>
        </div>
      ) : kyc.verdict === "rejected" ? (
        // Canceled with no reason attached: either the verdict predates the
        // change that kept the payload whole, or the provider sent none. Say
        // which so nobody reads a blank list as "nothing was wrong".
        <p className="subtle">
          No reason was recorded for this cancellation. Cases decided before 4
          Sep 2026 did not keep one.
        </p>
      ) : null}

      {kyc.caseId ? (
        <div className="row">
          <span className="subtle">Case</span>
          <span className="mono-inline">{kyc.caseId}</span>
          <CopyButton value={kyc.caseId} label="Copy case id" />
        </div>
      ) : null}
    </div>
  );
}

function FunnelSteps({ row }: { row: AdminReferralRow }) {
  return (
    <div className="steps">
      <Step done title="Signed up" detail={formatDate(row.signedUpAt)} />
      <Step
        done={row.inviteRedeemed}
        title="Invite redeemed"
        detail={row.inviteRedeemed ? "Code accepted" : "Waiting on the invite code"}
      />
      <Step
        done={row.kycStatus === "approved"}
        failed={row.kycStatus === "rejected"}
        title="KYC"
        detail={<KycChip status={row.kycStatus} />}
      />
      <Step
        done={row.cardIssued}
        title="Card issued"
        detail={row.cardIssued ? "Card exists" : "No card yet"}
      />
    </div>
  );
}

function Step({
  done,
  failed = false,
  title,
  detail,
}: {
  done: boolean;
  failed?: boolean;
  title: string;
  detail: ReactNode;
}) {
  const mark = failed ? "failed" : done ? "done" : "";
  return (
    <div className="step">
      <div className={`step-mark ${mark}`}>{failed ? "!" : done ? "✓" : ""}</div>
      <div className="step-text">
        <span className="step-title">{title}</span>
        <span className="subtle">{detail}</span>
      </div>
    </div>
  );
}

function UserOutcomes({ items, compact }: { items: AdminSendOutcome[]; compact: boolean }) {
  if (compact) {
    return (
      <ul className="list">
        {items.map((item) => (
          <li key={item.id} className="list-row">
            <span className="list-main">
              <span className="list-title">
                <span className="list-title-text">{item.title}</span>
              </span>
              <span className="list-meta">
                <span>{orDash(item.screen)}</span>
                <span>Sent {formatDateTime(item.sentAt)}</span>
              </span>
            </span>
            <span className="list-trail">
              <ReadChip item={item} short />
            </span>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Title</th>
            <th>Destination</th>
            <th className="num">Sent</th>
            <th>Read</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td className="cell-strong">{item.title}</td>
              <td>{orDash(item.screen)}</td>
              <td className="num">{formatDateTime(item.sentAt)}</td>
              <td>
                <ReadChip item={item} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
